/**
 * Helpers for the images embedded in a note's stored HTML.
 *
 * Notes keep their pictures two ways at once: inline as <img> tags in the note
 * body, and as rows in the matching image collection (LocationImage,
 * PortfolioImage, SiteImage) so they also appear in the gallery. Anything that
 * removes a picture therefore has to touch both, or the note keeps rendering an
 * image whose row is gone.
 */

/** Extracts every <img src> in document order. */
export const extractNoteImageUrls = (html: string | null | undefined): string[] => {
	if (!html) return []
	const urls: string[] = []
	const srcRegex = /<img[^>]+src="([^"]+)"[^>]*>/g
	let match
	while ((match = srcRegex.exec(html)) !== null) {
		urls.push(match[1])
	}
	return urls
}

/**
 * Cloudinary delivery URLs carry their transforms in the path
 * (`/upload/w_600,h_480,.../public_id`), so the same asset can be written one
 * way in a note and another in a gallery row. Comparing the transform-free
 * form is what lets a note image be matched back to its row.
 */
const VERSION = /^v\d+$/
/** `w_600,h_480,c_fill`, `c_fill`, `f_auto` — key_value, comma-joined. */
const TRANSFORM = /^[a-z]+_[^,/]+(?:,[a-z]+_[^,/]+)*$/i

const isTransformSegment = (segment: string): boolean =>
	Boolean(segment) && !VERSION.test(segment) && TRANSFORM.test(segment)

export const canonicalImageUrl = (url: string | null | undefined): string => {
	if (!url) return ''
	const trimmed = url.trim()
	if (!trimmed.includes('res.cloudinary.com')) return trimmed

	/*
	 * Drop the segment between /upload/ and the public id ONLY when it is
	 * actually a transform.
	 *
	 * This previously stripped whatever came next unconditionally, which on a
	 * URL with no transform ate the VERSION segment instead. A note URL
	 * carrying a transform then canonicalised to `/upload/v1/a.jpg` while its
	 * gallery row canonicalised to `/upload/a.jpg`, so `isSameImageUrl`
	 * returned false for the same picture — defeating the one case these
	 * helpers exist for, and silently: the symptom is a gallery row that
	 * survives its note being edited.
	 */
	return trimmed.replace(/\/upload\/([^/]+)\//, (match, segment) =>
		isTransformSegment(segment) ? '/upload/' : match,
	)
}

/** True when two URLs point at the same asset, ignoring transforms. */
export const isSameImageUrl = (a: string | null | undefined, b: string | null | undefined): boolean => {
	const left = canonicalImageUrl(a)
	return left !== '' && left === canonicalImageUrl(b)
}

/**
 * Removes every image from a note's HTML, leaving the text.
 *
 * Drops paragraphs that held nothing but an image, so stripping does not leave
 * a run of empty <p> behind.
 */
export const stripImagesFromNoteHtml = (html: string | null | undefined): string => {
	if (!html) return ''
	return html.replace(/<p>\s*<img[^>]*>\s*<\/p>/g, '').replace(/<img[^>]*>/g, '')
}

/**
 * A note split into its words and its pictures, in one pass.
 *
 * Notes store both in a single HTML string, which suits a wide card and not a
 * phone: rendered as-is the images come out inline, full width and stacked, so
 * five photos from one site visit put a screen and a half between a note's text
 * and the next note down.
 *
 * Composes the two helpers above rather than repeating their regexes. Both the
 * desktop card and the field feed need this split, and a second copy of "what
 * counts as an image" would drift.
 */
export const splitNoteContent = (
	html: string | null | undefined,
): { textHtml: string; imageUrls: string[] } => ({
	textHtml: stripImagesFromNoteHtml(html),
	imageUrls: extractNoteImageUrls(html),
})

/**
 * Removes one image from a note's HTML.
 *
 * Drops the whole wrapping paragraph when that paragraph held nothing but the
 * image — which is the shape the composer and the upload wizard both produce —
 * so removal does not leave an empty <p> behind. An image sitting inline
 * alongside text loses only the tag.
 */
export const removeImageFromNoteHtml = (html: string, url: string): string => {
	if (!html || !url) return html

	const matchesTarget = (tag: string) => {
		const src = /src="([^"]+)"/.exec(tag)?.[1]
		return isSameImageUrl(src, url)
	}

	// Paragraphs containing only this image (optionally with whitespace/breaks).
	const soloParagraph = /<p>(?:\s|<br\s*\/?>)*(<img[^>]*>)(?:\s|<br\s*\/?>)*<\/p>/g
	let next = html.replace(soloParagraph, (full, tag) => (matchesTarget(tag) ? '' : full))

	// Any remaining inline occurrence.
	next = next.replace(/<img[^>]*>/g, tag => (matchesTarget(tag) ? '' : tag))

	return next
}

/**
 * True when the note has no text and no images left — the state where the
 * caller should offer to delete the note rather than leave an empty card.
 */
export const isNoteHtmlEmpty = (html: string | null | undefined): boolean => {
	if (!html) return true
	if (/<img/i.test(html)) return false
	return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === ''
}
